import {
    ButtonGroup,
    Button,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import { MonetizationOn } from '@mui/icons-material';
import { BeableCourseType } from '@beable/roster-backend-client-v1';
import RosterBackendService from '../../../services/RosterBackendService';
import { getStyles } from './styles';

export interface BeableBuckLimitPerCourseRow {
  type: BeableCourseType;
  limit: number;
  label: string;
}

const RowTextField = ({
    value, onChange, icon, multiline = false
}: {
    field: any;
    value: unknown;
    icon: React.ReactNode;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    multiline?: boolean;
}) => {
    return (
        <TextField
            value={value}
            type="number"
            variant="outlined"
            fullWidth={false}
            size='small'
            onChange={onChange}
            onFocus={(event) => event.target.select()}
            slotProps={{
                input: {
                  readOnly: false,
                  multiline,
                  startAdornment: icon ?
                    <InputAdornment position="start">
                        {icon}
                    </InputAdornment> : undefined
                }
            }}
        />
    )
};

export default function QuestionHelpLessonLimits() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [ tableRows, setTableRows ] = useState<BeableBuckLimitPerCourseRow[]>([]);
    const [ modified, setModified ] = useState(false);
    const enabledCoursesForQuestionHelps: { [key in BeableCourseType]?: string} = {
      [BeableCourseType.ACADEMIC]: 'Academic',
      [BeableCourseType.SPECIAL_INTEREST]: 'Special Interest'
    }

    const fetchSpendingLimits = async () => {
      const getLimitsByCourseTypeRequest = await RosterBackendService.getMaxBucksToSpendOnHelpsByCourseType();
      const existingLimits = getLimitsByCourseTypeRequest.unwrapOr([]);
      const defaultTableRows = Object.values(BeableCourseType)
      .filter(courseType => courseType in enabledCoursesForQuestionHelps)
      .map<BeableBuckLimitPerCourseRow>(courseType => ({
        label: enabledCoursesForQuestionHelps[courseType]!,
        type: courseType,
        limit: existingLimits.find(e => e.courseType === courseType)?.buckLimit || 0
      }));
      setTableRows(defaultTableRows);
    };

    useEffect(() => {
      fetchSpendingLimits();
    }, []);

    const onSave = async () => {
      for( const { type, limit } of tableRows) {
        await RosterBackendService.saveQuestionHelpSpendingLimit({
          courseType: type,
          buckLimit: limit
      });
      }
      fetchSpendingLimits();
    };

    const handleChange = (
      type: BeableCourseType, 
      prop: keyof BeableBuckLimitPerCourseRow
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setModified(true);
      setTableRows(
          tableRows.map(row => { 
              if(row.type === type) {
                  return { 
                      ...row, 
                      [prop]: event.target.value
                  };
              }
              return row;
          })
      );
    };
    
    return (
        <>
            <Grid size={12}>
                <Grid container>
                    <Grid sx={styles.lessonLimitsContainer} size={12}>
                        <Typography variant="h5" align-left='true'>
                            Set Max Beable Bucks Spendable on Question Helps in each Lesson
                        </Typography>
                    </Grid>
                    <Grid sx={styles.lessonLimitsContainer} size={12}>
                        <p align-left='true'>
                          Each course type allows for a limit to be set on the number of Beable Bucks that can be used on Question Helps within a lesson
                        </p>
                        <ul>
                          <li>Question Helps are only enabled for <strong>Academic</strong> and <strong>Special Interest</strong> course types</li>
                          <li>Limits are per section of a lesson. Question Helps are only available in the <strong>Core Reading</strong> section currently</li>
                          <li>Limits are determined for users the first time they visit a lesson. If a user has already started a lesson the new limit will not take effect.</li>
                        </ul>
                    </Grid>
                </Grid>
                    <Grid>
                        <TableContainer sx={styles.lessonLimitsContainer}>
                            <Table sx={styles.table} aria-label="question help spending limits">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={styles.tableCell}>Course Type</TableCell>
                                        <TableCell sx={styles.tableCell}>Max Beable Bucks to Spend on Question Helps</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows.map(({ label, type, limit }) => (
                                        <TableRow key={type} data-cy={`table-row-${type}`}>
                                            <TableCell component="td" scope="row" sx={styles.tableCell}>
                                              {label}
                                            </TableCell>
                                            <TableCell component="td" scope="row" sx={styles.tableCell}>
                                                <RowTextField
                                                    value={limit}
                                                    field='limit'
                                                    icon={<MonetizationOn />}
                                                    onChange={handleChange(type, 'limit')}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    <Grid>
                        <ButtonGroup color="primary" sx={styles.actions}>
                            <Button
                                color='primary'
                                variant='contained'
                                disabled={!modified}
                                startIcon={<SaveIcon />}
                                type='submit'
                                data-cy="save-limits-button"
                                onClick={onSave}
                                
                            >
                                Save Limits
                            </Button>
                        </ButtonGroup>
                    </Grid>
            </Grid>
        </>
    );
}